/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

/**
 * AdapterUpload Component
 * Admin UI for uploading adapters to Modal via HuggingFace Hub
 *
 * Upload Pipeline:
 * 1. Local Kaggle Models → HuggingFace Hub (intermediate storage)
 * 2. HuggingFace Hub → Modal Volume (production deployment)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';
import './AdapterUpload.css';

// Vertical configuration with colors and icons
const VERTICAL_CONFIG = {
  wms: { label: 'WMS (Warehouse)', color: '#0891b2', icon: '📦' },
  iseries: { label: 'iSeries (AS/400)', color: '#7C3AED', icon: '🖥️' },
  civil: { label: 'Civil Construction', color: '#F59E0B', icon: '🏗️' },
  stitching: { label: 'Stitching & Apparel', color: '#10B981', icon: '👔' },
  erpnext: { label: 'ERPNext (ERP System)', color: '#F59E0B', icon: '🏭' },
  'platform-sales': { label: 'Platform Sales', color: '#dc2626', icon: '💼' },
  'platform-assist': { label: 'Platform Assist', color: '#2563eb', icon: '🤖' },
  skillcode: { label: 'SkillCode (AI Software Engineering)', color: '#7C3AED', icon: '🖥️' }
};

const AdapterUpload = () => {
  const navigate = useNavigate();

  // State
  const [config, setConfig] = useState(null);
  const [adapters, setAdapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVertical, setSelectedVertical] = useState('all');
  const [selectedAdapter, setSelectedAdapter] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  // Load configuration and adapters
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load config and adapters in parallel
      const [configRes, adaptersRes] = await Promise.all([
        adminApi.getAdapterUploadConfig(),
        adminApi.getLocalAdapters()
      ]);

      setConfig(configRes.data);
      setAdapters(adaptersRes.data || []);

    } catch (err) {
      console.error('[AdapterUpload] Load error:', err);
      setError(err.response?.data?.error || 'Failed to load adapter data');
    } finally {
      setLoading(false);
    }
  };

  // Filter adapters by vertical
  const filteredAdapters = selectedVertical === 'all'
    ? adapters
    : adapters.filter(a => a.vertical === selectedVertical);

  // Handle adapter selection
  const handleSelectAdapter = (adapter) => {
    setSelectedAdapter(adapter);
    setUploadResult(null);
  };

  // Handle upload to HuggingFace only
  const handleUploadHF = async () => {
    if (!selectedAdapter) return;

    try {
      setUploadProgress({ step: 'huggingface', status: 'uploading' });
      setError('');

      const result = await adminApi.uploadAdapterToHF({
        vertical: selectedAdapter.vertical,
        adapterName: selectedAdapter.name,
        version: 'v1'
      });

      setUploadProgress(null);
      setUploadResult({
        success: result.success,
        step: 'huggingface',
        data: result.data
      });

    } catch (err) {
      setUploadProgress(null);
      setError(err.response?.data?.error || 'Upload to HuggingFace failed');
    }
  };

  // Handle sync to Modal
  const handleSyncModal = async () => {
    if (!selectedAdapter || !uploadResult?.data?.hfPath) return;

    try {
      setUploadProgress({ step: 'modal', status: 'syncing' });
      setError('');

      const result = await adminApi.syncAdapterToModal({
        vertical: selectedAdapter.vertical,
        adapterName: selectedAdapter.name,
        hfPath: uploadResult.data.hfPath
      });

      setUploadProgress(null);
      setUploadResult(prev => ({
        ...prev,
        modalSync: result.data
      }));

    } catch (err) {
      setUploadProgress(null);
      setError(err.response?.data?.error || 'Modal sync failed');
    }
  };

  // Handle full pipeline upload
  const handleFullUpload = async () => {
    if (!selectedAdapter) return;

    if (!window.confirm(
      `Upload ${selectedAdapter.name} to production?\n\n` +
      `This will:\n` +
      `1. Upload to HuggingFace Hub\n` +
      `2. Sync to Modal Volume\n\n` +
      `Size: ${selectedAdapter.sizeFormatted}`
    )) {
      return;
    }

    try {
      setUploadProgress({ step: 'full', status: 'starting' });
      setError('');

      const result = await adminApi.uploadAdapterFull({
        vertical: selectedAdapter.vertical,
        adapterName: selectedAdapter.name,
        version: 'v1'
      });

      setUploadProgress(null);
      setUploadResult({
        success: result.success,
        step: 'full',
        data: result.data
      });

      // Reload adapters to reflect changes
      loadData();

    } catch (err) {
      setUploadProgress(null);
      setError(err.response?.data?.error || 'Full upload pipeline failed');
    }
  };

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return '--';
    const mb = bytes / (1024 * 1024);
    if (mb > 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout showBackButton={true} backPath="/admin/training/adapters">
      <div className="adapter-upload">
        {/* Header */}
        <div className="page-header">
          <div className="header-left">
            <h1>📤 Adapter Upload</h1>
            <p className="page-subtitle">
              Upload adapters to Modal via HuggingFace Hub
            </p>
          </div>
          <div className="header-right">
            <button
              className="refresh-button"
              onClick={loadData}
              disabled={loading}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

{/* Configuration Status */}
        {config && (
          <div className="config-status">
            {/* Local Path - only show in development */}
            {!config.isProduction && (
              <div className="config-item">
                <span className="config-label">Local Path:</span>
                <span className={`config-value ${config.kagglePathExists ? 'success' : 'error'}`}>
                  {config.kagglePathExists ? '✅' : '❌'} {config.kaggleModelsPath}
                </span>
              </div>
            )}

            {/* Production environment indicator */}
            {config.isProduction && (
              <div className="config-item">
                <span className="config-label">Environment:</span>
                <span className="config-value info">
                  ☁️ Cloud (adapters from database)
                </span>
              </div>
            )}

            {/* HuggingFace - always shown */}
            <div className="config-item">
              <span className="config-label">HuggingFace:</span>
              <span className={`config-value ${config.hfConfigured ? 'success' : 'warning'}`}>
                {config.hfConfigured ? '✅ Configured' : '⚠️ Not configured'}
                {config.hfRepoId && ` (${config.hfRepoId})`}
              </span>
            </div>

            {/* Modal Sync - always shown */}
            <div className="config-item">
              <span className="config-label">Modal Sync:</span>
              <span className={`config-value ${config.modalSyncConfigured ? 'success' : 'info'}`}>
                {config.modalSyncConfigured ? '✅ Configured' : 'ℹ️ Manual sync required'}
              </span>
            </div>
          </div>
        )}
        
        {/* Error Alert */}
        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Upload Progress */}
        {uploadProgress && (
          <div className="upload-progress">
            <div className="progress-spinner"></div>
            <span className="progress-text">
              {uploadProgress.step === 'huggingface' && 'Uploading to HuggingFace Hub...'}
              {uploadProgress.step === 'modal' && 'Syncing to Modal Volume...'}
              {uploadProgress.step === 'full' && 'Running full upload pipeline...'}
            </span>
          </div>
        )}

        {/* Main Content */}
        <div className="upload-content">
          {/* Left Panel: Adapter List */}
          <div className="adapter-list-panel">
            <div className="panel-header">
              <h2>{config?.isProduction ? 'Registered Adapters' : 'Local Adapters'}</h2>

              
              <select
                value={selectedVertical}
                onChange={(e) => setSelectedVertical(e.target.value)}
                className="vertical-filter"
              >
                <option value="all">All Verticals</option>
                {Object.entries(VERTICAL_CONFIG).map(([key, cfg]) => (
                  <option key={key} value={key}>
                    {cfg.icon} {cfg.label}
                  </option>
                ))}
              </select>
            </div>

            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading adapters...</p>
              </div>
            ) : filteredAdapters.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">📁</span>
                {config?.isProduction ? (
                <>
                  <p>No adapters registered in database</p>
                  <p className="hint">
                    Upload adapters via HuggingFace, then sync to Modal.
                  </p>
                </>
                ) : (
                <>
                <p>No adapters found in local directory</p>
                <p className="hint">
                  Download adapters from Kaggle to:{' '}
                  <code>{config?.kaggleModelsPath || './training/models'}</code>
                </p>
                </>
                )}

              </div>
            ) : (
              <div className="adapter-list">
                {filteredAdapters.map((adapter, idx) => {
                  const verticalCfg = VERTICAL_CONFIG[adapter.vertical] || {};
                  const isSelected = selectedAdapter?.name === adapter.name &&
                                    selectedAdapter?.vertical === adapter.vertical;

                  return (
                    <div
                      key={`${adapter.vertical}-${adapter.name}-${idx}`}
                      className={`adapter-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectAdapter(adapter)}
                    >
                      <div className="adapter-icon" style={{ background: verticalCfg.color }}>
                        {verticalCfg.icon || '🔌'}
                      </div>
                      <div className="adapter-info">
                        <span className="adapter-name">{adapter.name}</span>
                        <span className="adapter-meta">
                          {verticalCfg.label} • {adapter.sizeFormatted}
                        </span>
                      </div>
                      <div className="adapter-files">
                        {adapter.fileCount} files
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Panel: Selected Adapter Details */}
          <div className="adapter-detail-panel">
            {!selectedAdapter ? (
              <div className="no-selection">
                <span className="selection-icon">👈</span>
                <p>Select an adapter from the list</p>
              </div>
            ) : (
              <>
                <div className="detail-header">
                  <div
                    className="detail-icon"
                    style={{ background: VERTICAL_CONFIG[selectedAdapter.vertical]?.color }}
                  >
                    {VERTICAL_CONFIG[selectedAdapter.vertical]?.icon || '🔌'}
                  </div>
                  <div>
                    <h2>{selectedAdapter.name}</h2>
                    <p className="detail-vertical">
                      {VERTICAL_CONFIG[selectedAdapter.vertical]?.label || selectedAdapter.vertical}
                    </p>
                  </div>
                </div>

                {/* Adapter Configuration */}
                <div className="detail-section">
                  <h3>Configuration</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Base Model</span>
                      <span className="value">{selectedAdapter.config?.baseModel || 'Unknown'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">PEFT Type</span>
                      <span className="value">{selectedAdapter.config?.peftType || 'LORA'}</span>
                    </div>
                    {selectedAdapter.config?.rank && (
                      <div className="detail-item">
                        <span className="label">LoRA Rank</span>
                        <span className="value">{selectedAdapter.config.rank}</span>
                      </div>
                    )}
                    {selectedAdapter.config?.alpha && (
                      <div className="detail-item">
                        <span className="label">LoRA Alpha</span>
                        <span className="value">{selectedAdapter.config.alpha}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Files */}
                <div className="detail-section">
                  <h3>Files ({selectedAdapter.fileCount})</h3>
                  <div className="file-list">
                    {selectedAdapter.files?.map((file, idx) => (
                      <div key={idx} className="file-item">
                        <span className="file-icon">📄</span>
                        <span className="file-name">{file}</span>
                      </div>
                    ))}
                  </div>
                  <div className="file-summary">
                    <span>Total Size: <strong>{selectedAdapter.sizeFormatted}</strong></span>
                    <span>Modified: <strong>{formatDate(selectedAdapter.modifiedAt)}</strong></span>
                  </div>
                </div>

                {/* Upload Actions */}
                <div className="detail-section actions-section">
                  <h3>Upload Actions</h3>

                  {/* Full Pipeline Button */}
                  <button
                    className="action-button primary"
                    onClick={handleFullUpload}
                    disabled={uploadProgress || !config?.hfConfigured}
                  >
                    🚀 Full Pipeline Upload
                    <span className="button-hint">Local → HuggingFace → Modal</span>
                  </button>

                  {/* Individual Steps */}
                  <div className="step-buttons">
                    <button
                      className="action-button secondary"
                      onClick={handleUploadHF}
                      disabled={uploadProgress || !config?.hfConfigured}
                    >
                      ☁️ Upload to HuggingFace
                    </button>
                    <button
                      className="action-button secondary"
                      onClick={handleSyncModal}
                      disabled={uploadProgress || !uploadResult?.data?.hfPath || !config?.modalSyncConfigured}
                    >
                      📥 Sync to Modal
                    </button>
                  </div>

                  {!config?.hfConfigured && (
                    <div className="warning-message">
                      ⚠️ HuggingFace not configured. Set HF_TOKEN and HF_MODAL_UPLOAD environment variables.
                    </div>
                  )}
                </div>

                {/* Upload Result */}
                {uploadResult && (
                  <div className={`upload-result ${uploadResult.success ? 'success' : 'error'}`}>
                    <h3>{uploadResult.success ? '✅ Upload Successful' : '❌ Upload Failed'}</h3>

                    {uploadResult.data?.hfUrl && (
                      <div className="result-item">
                        <span className="label">HuggingFace URL:</span>
                        <a
                          href={uploadResult.data.hfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="result-link"
                        >
                          {uploadResult.data.hfUrl}
                        </a>
                      </div>
                    )}

                    {uploadResult.data?.modalPath && (
                      <div className="result-item">
                        <span className="label">Modal Path:</span>
                        <code>{uploadResult.data.modalPath}</code>
                      </div>
                    )}

                    {uploadResult.data?.files && (
                      <div className="result-item">
                        <span className="label">Files Uploaded:</span>
                        <span>{uploadResult.data.files.length}</span>
                      </div>
                    )}

                    {uploadResult.data?.steps && (
                      <div className="pipeline-steps">
                        {uploadResult.data.steps.map((step, idx) => (
                          <div key={idx} className={`step-item ${step.status}`}>
                            <span className="step-icon">
                              {step.status === 'success' ? '✅' : '❌'}
                            </span>
                            <span className="step-name">{step.step}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdapterUpload;
